import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Datepicker, GridContainer, GridItem } from '../../../components/shared';
import { MESSAGE } from '../../../constants/message';
import { Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Warning } from '@mui/icons-material';
import useStyles from '../AddCollection/style';
import { getLastCollectionDate } from '../../../utils/collection-methods';
import { checkValueNotNullUndefinedBlank, getDateFormat } from '../../../utils/common-methods';
import { routeConstants } from '../../../constants/routeConstants';
import { useGetDatesOfReadingQuery } from '../../../services/CollectionService/CollectionService';
import { useNavigate } from 'react-router-dom';
const ApprovedSublocationDateModule = ({ sublocation, hasWarning = false }) => {
  const [dateOfReading, setDateOfReading] = useState(sublocation?.dateOfReading);
  const [dateEntered, setDateEntered] = useState(sublocation?.dateEntered);
  const classes = useStyles();
  const theme = useTheme();
  const isBelowLG = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const storedSublocationId = useRef(null);
  const sublocationID = storedSublocationId.current || sublocation.id;
  const shouldFetchHistory = Boolean(sublocationID);
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (sublocationID) {
      storedSublocationId.current = sublocationID;
    }
  }, [sublocationID]);
  const { data } = useGetDatesOfReadingQuery({ sublocationId: sublocationID }, { skip: !shouldFetchHistory });
  const fetchCollectionDetails = (date) => {
    if (!data?.datesOfReadingData) return;
    const normalizedDate = new Date(date)?.toISOString().split('T')[0];
    const collection = data.datesOfReadingData.find((d) => {
      const apiDate = new Date(d.date)?.toISOString().split('T')[0];
      return apiDate === normalizedDate;
    });
    if (collection) {
      const { transactionId } = collection;
      setTimeout(() => {
        navigate(`/${routeConstants.COLLECTIONS_ROUTE}/approved/${transactionId}`, {
          redirect: true,
        });
      }, 1000);
    }
  };
  const changeDate = (offset) => {
    if (!data?.datesOfReadingData?.length) return;

    const sortedDates = [...data.datesOfReadingData].map((d) => new Date(d.date).toISOString().split('T')[0]);
    const [month, day, year] = dateOfReading.split('/');
    const formattedDate = `${year}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}`;
    let index = sortedDates.indexOf(formattedDate);
    if (offset === 'Left') {
      const newDateOfReading = sortedDates[--index];
      setDateOfReading(newDateOfReading);
    } else {
      const newDateOfReading = sortedDates[++index];
      setDateOfReading(newDateOfReading);
    }
  };
  const { sortedDates, currentIndex } = useMemo(() => {
    const datesArray = data?.datesOfReadingData?.map((d) => new Date(d.date).toISOString().split('T')[0]) || [];
    // Convert dateOfReading (MM/DD/YYYY) to YYYY-MM-DD format
    let currIndex = -1;
    if (dateOfReading && datesArray.length > 0) {
      const [month, day, year] = dateOfReading.split('/');
      const formattedDate = `${year}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}`;
      currIndex = datesArray.indexOf(formattedDate);
    }
    return { sortedDates: datesArray, currentIndex: currIndex };
  }, [data, dateOfReading]);

  // Determine if left or right arrows should be disabled.
  const isLeftDisabled = currentIndex <= 0;
  // if index is -1 or at the last element, disable right arrow
  const isRightDisabled = currentIndex === -1 || currentIndex >= sortedDates.length - 1;

  useEffect(() => {
    if (dateOfReading) {
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        fetchCollectionDetails(dateOfReading);
      }
    }
  }, [dateOfReading]);

  useEffect(() => {
    setDateOfReading(sublocation?.dateOfReading);
    setDateEntered(sublocation?.dateEntered);
  }, [sublocation]);
  const stopIndicatorInteraction = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const lastCollectionDate = getLastCollectionDate(sublocation, true);
  return (
    <GridContainer className={classes.dateContainer}>
      <GridItem xs={12} sm={12} md={12} lg={4} py={isBelowLG ? 1 : 0}>
        <Typography data-testid="accordionTitle" variant="subtitle5">
          {sublocation?.name}
        </Typography>
      </GridItem>
      <GridItem ml={2} style={{ cursor: 'pointer' }}>
        <ChevronLeftIcon
          fontSize="large"
          onClick={(event) => {
            // Only proceed if not disabled
            if (!isLeftDisabled) {
              event.stopPropagation();
              event.preventDefault();
              changeDate('Left');
            }
          }}
          style={{
            cursor: isLeftDisabled ? 'not-allowed' : 'pointer',
            opacity: isLeftDisabled ? 0.3 : 1,
          }}
        />
      </GridItem>
      <GridItem xs={12} sm={3.5} md={4} lg={2} my={1} className={classes.datePointerEvent}>
        <Datepicker
          label={MESSAGE.DATE_OF_READING}
          name="dateOfReading"
          id="dateOfReading"
          onChange={() => {}}
          value={dateOfReading}
          inputFormat="MM/DD/YYYY"
          disabled
        />
      </GridItem>
      <GridItem mr={2} style={{ cursor: 'pointer' }}>
        <ChevronRightIcon
          id="right"
          fontSize="large"
          onClick={(event) => {
            if (!isRightDisabled) {
              event.stopPropagation();
              event.preventDefault();
              changeDate('Right');
            }
          }}
          style={{
            cursor: isRightDisabled ? 'not-allowed' : 'pointer',
            opacity: isRightDisabled ? 0.3 : 1,
          }}
        />
      </GridItem>
      <GridItem
        xs={12}
        sm={3.5}
        md={4}
        lg={2}
        mr={3}
        my={1}
        className={classes.datePointerEvent}
        onClick={(e) => e.stopPropagation()}
      >
        <Datepicker
          label={MESSAGE.DATE_ENTERED}
          name="dateEntered"
          onChange={() => {}}
          value={dateEntered}
          id="dateEntered"
          inputFormat="MM/DD/YYYY"
          disabled
        />
      </GridItem>
      <GridItem xs={12} sm={3.5} md={3} lg={2}>
        <Typography variant="subtitle2">{`Last Collection - ${
          checkValueNotNullUndefinedBlank(lastCollectionDate) ? getDateFormat(lastCollectionDate) : 'N/A'
        }`}</Typography>
      </GridItem>
      {hasWarning && (
        <GridItem xs="auto" display="flex" alignItems="center">
          <button
            type="button"
            tabIndex={-1}
            aria-label={MESSAGE.VARIANCE_FOUND_TOOLTIP}
            onClick={stopIndicatorInteraction}
            onMouseDown={stopIndicatorInteraction}
            style={{
              display: 'inline-flex',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'default',
            }}
          >
            <Tooltip
              title={MESSAGE.VARIANCE_FOUND_TOOLTIP}
              placement="top-end"
              enterDelay={50}
              leaveDelay={150}
              enterNextDelay={0}
              disableInteractive
              componentsProps={{
                popper: {
                  sx: {
                    '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                      marginBottom: '2px !important',
                    },
                  },
                },
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    color: '#111827',
                    border: '1px solid #b9c7d8',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  },
                },
              }}
            >
              <Warning data-testid="sublocation-warning-icon" color="error" fontSize="small" />
            </Tooltip>
          </button>
        </GridItem>
      )}
    </GridContainer>
  );
};
export default React.memo(ApprovedSublocationDateModule);
